<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import {
  SOURCES,
  STATUS_BADGE_CLASSES,
  fetchJson,
  normalizeTicimax,
  normalizeShopify,
  normalizeTrendyol,
  formatDate,
  formatCurrency,
} from '../helpers/orderHelpers';
import { fetchMarketplaceJson as fetchMarketplace } from 'dashboard/helpers/marketplaceApi';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const fetchMarketplaceJson = path =>
  fetchMarketplace(route.params.accountId, path);

const PAGE_SIZE = 20;

// --- Sunucu tarafinda sayfalanan, tum kanallari tek sorguda birlestiren liste ---
// Eskiden Ticimax (offset) + Shopify (cursor) + Trendyol (ayri 14-gunluk fetch)
// istemci tarafinda elle birlestiriliyordu; artik hepsi yerel DB'den (marketplace_orders)
// tek bir Rails sorgusuyla, zaten siralanmis ve sayfalanmis olarak geliyor.
const isLoading = ref(true);
const isLoadingMore = ref(false);
const loadError = ref('');
const pageOrders = ref([]);
const currentPage = ref(1);
const hasMore = ref(false);

const statusFilter = ref('all');
const channelFilter = ref('all');

const CHANNEL_TABS = [
  { key: 'all', label: 'Tümü' },
  { key: 'sites', label: 'Tüm Siteler' },
  { key: 'ticimax', label: 'Ticimax · Koza' },
  { key: 'trendyol_koza', label: 'Trendyol · Koza' },
  { key: 'shopify', label: 'Shopify · Modax' },
  { key: 'trendyol_modax', label: 'Trendyol · Modax' },
];

const CHANNEL_PARAM = { sites: 'ticimax,shopify' };

const normalizeByChannel = (channel, order, commissionAmount) => {
  if (channel === 'shopify') return normalizeShopify(order);
  if (channel === 'trendyol_koza' || channel === 'trendyol_modax')
    return normalizeTrendyol(order, channel, commissionAmount);
  return normalizeTicimax(order);
};

const fetchOrdersPage = async page => {
  const offset = (page - 1) * PAGE_SIZE;
  const params = new URLSearchParams({
    limit: String(PAGE_SIZE),
    offset: String(offset),
  });
  const channelParam =
    CHANNEL_PARAM[channelFilter.value] || channelFilter.value;
  if (channelParam !== 'all') params.set('channel', channelParam);
  const data = await fetchMarketplaceJson(`orders?${params.toString()}`);
  hasMore.value = !!data.hasMore;
  return (data.orders || []).map(item =>
    normalizeByChannel(item.channel, item.order, item.commissionAmount)
  );
};

const loadOrders = async () => {
  isLoading.value = true;
  loadError.value = '';
  currentPage.value = 1;
  try {
    pageOrders.value = await fetchOrdersPage(1);
  } catch (error) {
    loadError.value = t('ORDERS.ERRORS.BOTH_FAILED');
    pageOrders.value = [];
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadOrders);

watch(channelFilter, loadOrders);

const goToPage = async page => {
  if (page < 1 || page === currentPage.value) return;
  isLoadingMore.value = true;
  try {
    pageOrders.value = await fetchOrdersPage(page);
    currentPage.value = page;
  } finally {
    isLoadingMore.value = false;
  }
};

const currentPageOrders = computed(() => pageOrders.value);

// --- Müşteri arama: sipariş no / telefon / ad soyad / kargo takip no ---
// Ticimax tarafinda backend gercek gecmisi tarayan bir arama yapar (proxy uzerinden);
// Shopify tarafinda once musteri adina gore eslesen musteriler bulunur (Shopify'in
// kendi isim arama API'si), sonra o musterilerin TUM siparis gecmisi cekilir —
// boylece "ayse" aramasi tum Ayse'leri ve gecmis siparislerini getirir.
const searchQuery = ref('');
const isSearching = ref(false);
const hasSearched = ref(false);
const searchResults = ref([]);
const searchError = ref('');

// Bu magazada Shopify siparis numaralari (ör. #107137 / 7137) en fazla 8 hane;
// telefon numaralari ise (alan kodu/ulke kodu ile) en az 10 hane oluyor — bu
// yuzden uzun rakam dizilerini siparis no aramasina degil musteri/telefon
// aramasina yonlendirip "5517124001 gibi telefonlar bulunamiyor" sorununu onleriz.
const looksLikeOrderNumber = value => /^#?\d{3,8}$/.test(value.trim());

// Shopify musteri arama uctan ucca telefon formatina duyarli: "+90 533 254 68 91"
// gibi '+' ve bosluk iceren degerlerde 0 sonuc donuyor, ama rakam-only halinde
// ("905332546891") dogru musterileri buluyor — bu yuzden telefon gibi gorunen
// sorgularda yalnizca rakamlari gonderiyoruz.
const normalizeForCustomerSearch = value => {
  const trimmed = value.trim();
  const digits = trimmed.replace(/\D/g, '');
  return /^[+\d][\d\s().-]*$/.test(trimmed) && digits.length >= 7
    ? digits
    : trimmed;
};

const searchShopify = async query => {
  const trimmed = query.trim();
  const found = new Map();

  if (looksLikeOrderNumber(trimmed)) {
    const name = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
    const data = await fetchJson(
      `/shopify-api/orders.json?status=any&name=${encodeURIComponent(name)}`
    );
    (data.orders || []).forEach(order => found.set(order.id, order));
    return Array.from(found.values()).map(normalizeShopify);
  }

  const customersData = await fetchJson(
    `/shopify-api/customers/search.json?query=${encodeURIComponent(normalizeForCustomerSearch(trimmed))}&limit=15`
  );
  const customers = customersData.customers || [];
  const orderLists = await Promise.all(
    customers.map(customer =>
      fetchJson(
        `/shopify-api/customers/${customer.id}/orders.json?status=any`
      ).catch(() => ({ orders: [] }))
    )
  );
  orderLists.forEach(({ orders: customerOrders }) =>
    (customerOrders || []).forEach(order => found.set(order.id, order))
  );
  return Array.from(found.values()).map(normalizeShopify);
};

const runSearch = async () => {
  const query = searchQuery.value.trim();
  if (!query) return;

  isSearching.value = true;
  hasSearched.value = true;
  searchError.value = '';
  searchResults.value = [];

  const [ticimaxResult, shopifyResult] = await Promise.allSettled([
    fetchJson(`/ticimax-api/siparisler?q=${encodeURIComponent(query)}`),
    searchShopify(query),
  ]);

  const merged = [];
  if (ticimaxResult.status === 'fulfilled')
    merged.push(...(ticimaxResult.value.orders || []).map(normalizeTicimax));
  if (shopifyResult.status === 'fulfilled') merged.push(...shopifyResult.value);
  merged.sort((a, b) => new Date(b.date) - new Date(a.date));
  searchResults.value = merged;

  if (
    ticimaxResult.status === 'rejected' &&
    shopifyResult.status === 'rejected'
  ) {
    searchError.value = t('ORDERS.ERRORS.BOTH_FAILED');
  }
  isSearching.value = false;
};

const clearSearch = () => {
  searchQuery.value = '';
  hasSearched.value = false;
  searchResults.value = [];
  searchError.value = '';
};

const isSearchMode = computed(() => hasSearched.value);

const visibleOrders = computed(() => {
  const source = isSearchMode.value
    ? searchResults.value
    : currentPageOrders.value;
  if (statusFilter.value === 'all') return source;
  return source.filter(order => order.statusBucket === statusFilter.value);
});

const openOrder = order => {
  const isTrendyol =
    order.source === 'trendyol_koza' || order.source === 'trendyol_modax';
  router.push({
    name: 'orders_detail',
    params: { source: order.source, orderId: order.id },
    ...(isTrendyol
      ? {
          state: {
            rawOrder: JSON.stringify(order.raw),
            correctedCommission: order.correctedCommission,
          },
        }
      : {}),
  });
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <header
      class="flex items-center justify-between gap-4 px-4 py-4 border-b sm:px-6 border-n-weak flex-shrink-0"
    >
      <div class="min-w-0">
        <h1 class="text-xl font-medium text-n-slate-12">
          {{ t('ORDERS.TITLE') }}
        </h1>
        <p class="text-sm truncate text-n-slate-11">
          {{ t('ORDERS.SUBTITLE') }}
        </p>
      </div>
      <button
        type="button"
        class="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg text-n-slate-11 outline outline-1 outline-n-weak hover:bg-n-alpha-1 transition-colors flex-shrink-0"
        :disabled="isLoading"
        @click="loadOrders"
      >
        <span
          class="size-4 i-lucide-refresh-cw"
          :class="{ 'animate-spin': isLoading }"
        />
        <span class="hidden sm:inline">{{ t('ORDERS.REFRESH') }}</span>
      </button>
    </header>

    <!-- Kanal filtre sekmeleri -->
    <div
      class="flex items-center gap-1 px-4 sm:px-6 pt-3 pb-0 border-b border-n-weak flex-shrink-0 overflow-x-auto"
    >
      <button
        v-for="tab in CHANNEL_TABS"
        :key="tab.key"
        class="px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 transition-colors"
        :class="
          channelFilter === tab.key
            ? 'border-woot-500 text-woot-600 dark:text-woot-400'
            : 'border-transparent text-n-slate-10 hover:text-n-slate-12 hover:border-n-slate-6'
        "
        @click="channelFilter = tab.key"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Müşteri arama bölümü -->
    <section
      class="px-4 py-4 border-b sm:px-6 border-n-weak flex-shrink-0 bg-n-solid-1"
    >
      <h2 class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
        <span class="size-4 i-lucide-search-check text-n-slate-10" />
        {{ t('ORDERS.SEARCH.TITLE') }}
      </h2>
      <p class="mt-1 text-xs text-n-slate-10">{{ t('ORDERS.SEARCH.HINT') }}</p>
      <form
        class="flex flex-col gap-2 mt-3 sm:flex-row"
        @submit.prevent="runSearch"
      >
        <div class="relative flex-1">
          <span
            class="absolute -translate-y-1/2 size-4 i-lucide-search left-3 top-1/2 text-n-slate-10"
          />
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="t('ORDERS.SEARCH.PLACEHOLDER')"
            class="w-full py-2 pl-9 pr-3 text-sm border rounded-lg bg-n-alpha-1 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
        </div>
        <div class="flex gap-2">
          <button
            type="submit"
            class="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand hover:opacity-90 disabled:opacity-50"
            :disabled="isSearching || !searchQuery.trim()"
          >
            <span
              v-if="isSearching"
              class="size-4 border-2 rounded-full animate-spin border-white/40 border-t-white"
            />
            <span v-else class="size-4 i-lucide-search" />
            {{ t('ORDERS.SEARCH.BUTTON') }}
          </button>
          <button
            v-if="hasSearched"
            type="button"
            class="px-4 py-2 text-sm font-medium rounded-lg text-n-slate-11 outline outline-1 outline-n-weak hover:bg-n-alpha-1"
            @click="clearSearch"
          >
            {{ t('ORDERS.SEARCH.CLEAR') }}
          </button>
        </div>
      </form>
      <p
        v-if="isSearching"
        class="flex items-center gap-2 mt-3 text-xs text-n-amber-11"
      >
        <span class="size-3.5 i-lucide-loader-circle animate-spin" />
        {{ t('ORDERS.SEARCH.SEARCHING') }}
      </p>
      <p
        v-else-if="hasSearched && !searchError"
        class="mt-3 text-xs text-n-slate-11"
      >
        {{ t('ORDERS.SEARCH.RESULTS_COUNT', { count: searchResults.length }) }}
      </p>
      <p v-if="searchError" class="mt-3 text-xs text-n-ruby-10">
        {{ searchError }}
      </p>
    </section>

    <div
      class="flex flex-wrap items-center gap-3 px-4 py-3 border-b sm:px-6 border-n-weak flex-shrink-0"
    >
      <select
        v-model="statusFilter"
        class="px-3 py-2 text-sm border rounded-lg bg-n-alpha-1 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
      >
        <option value="all">{{ t('ORDERS.STATUS_FILTER.ALL') }}</option>
        <option value="success">{{ t('ORDERS.STATUS_FILTER.SUCCESS') }}</option>
        <option value="pending">{{ t('ORDERS.STATUS_FILTER.PENDING') }}</option>
        <option value="cancelled">
          {{ t('ORDERS.STATUS_FILTER.CANCELLED') }}
        </option>
      </select>
      <div class="flex items-center gap-4 ml-auto text-xs text-n-slate-11">
        <span class="flex items-center gap-1.5">
          <span class="rounded-full size-2.5" :class="SOURCES.ticimax.dot" />
          {{ SOURCES.ticimax.label }} ({{ SOURCES.ticimax.sub }})
        </span>
        <span class="flex items-center gap-1.5">
          <span class="rounded-full size-2.5" :class="SOURCES.shopify.dot" />
          {{ SOURCES.shopify.label }} ({{ SOURCES.shopify.sub }})
        </span>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto">
      <div
        v-if="isLoading"
        class="flex flex-col items-center justify-center h-full gap-3 text-n-slate-11"
      >
        <Spinner />
        <p class="text-sm">{{ t('ORDERS.LOADING') }}</p>
      </div>

      <div
        v-else-if="loadError && !visibleOrders.length"
        class="flex flex-col items-center justify-center h-full gap-3 text-n-slate-11"
      >
        <span class="size-9 i-lucide-alert-triangle text-n-ruby-9" />
        <p class="text-sm">{{ loadError }}</p>
        <button
          type="button"
          class="px-3 py-1.5 text-sm font-medium rounded-lg outline outline-1 outline-n-weak hover:bg-n-alpha-1"
          @click="loadOrders"
        >
          {{ t('ORDERS.RETRY') }}
        </button>
      </div>

      <div
        v-else-if="!visibleOrders.length"
        class="flex flex-col items-center justify-center h-full gap-2 text-n-slate-11"
      >
        <span class="size-9 i-lucide-package-search" />
        <p class="text-sm">
          {{ isSearchMode ? t('ORDERS.SEARCH.NO_RESULTS') : t('ORDERS.EMPTY') }}
        </p>
      </div>

      <table v-else class="w-full text-sm border-collapse">
        <thead
          class="sticky top-0 z-10 text-xs tracking-wide uppercase border-b bg-n-background border-n-weak text-n-slate-10"
        >
          <tr>
            <th class="px-4 py-2.5 text-start font-medium sm:px-6">
              {{ t('ORDERS.COLUMNS.ORDER_NO') }}
            </th>
            <th class="px-4 py-2.5 text-start font-medium">
              {{ t('ORDERS.COLUMNS.CUSTOMER') }}
            </th>
            <th class="px-4 py-2.5 text-start font-medium">
              {{ t('ORDERS.COLUMNS.AMOUNT') }}
            </th>
            <th class="hidden px-4 py-2.5 text-start font-medium sm:table-cell">
              {{ t('ORDERS.COLUMNS.DATE') }}
            </th>
            <th class="px-4 py-2.5 text-start font-medium">
              {{ t('ORDERS.COLUMNS.STATUS') }}
            </th>
            <th class="hidden px-4 py-2.5 text-start font-medium lg:table-cell">
              {{ t('ORDERS.COLUMNS.PAYMENT') }}
            </th>
            <th
              class="hidden px-4 py-2.5 text-start font-medium sm:table-cell sm:px-6"
            >
              {{ t('ORDERS.COLUMNS.SOURCE') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="order in visibleOrders"
            :key="order.key"
            class="transition-colors border-b border-l-4 cursor-pointer border-n-weak hover:bg-n-alpha-1"
            :class="SOURCES[order.source].border"
            @click="openOrder(order)"
          >
            <td class="px-4 py-3 font-medium sm:px-6 text-n-slate-12">
              {{ order.orderNo }}
              <span
                class="block size-2 mt-1 rounded-full sm:hidden"
                :class="SOURCES[order.source].dot"
              />
            </td>
            <td class="px-4 py-3">
              <div class="text-n-slate-12">{{ order.customerName || '—' }}</div>
              <div class="text-xs text-n-slate-10">
                {{ order.phone || '—' }}
              </div>
            </td>
            <td class="px-4 py-3 font-medium text-n-slate-12 whitespace-nowrap">
              {{ formatCurrency(order.amount, order.currency) }}
            </td>
            <td
              class="hidden px-4 py-3 sm:table-cell text-n-slate-11 whitespace-nowrap"
            >
              {{ formatDate(order.date) }}
            </td>
            <td class="px-4 py-3">
              <span
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                :class="STATUS_BADGE_CLASSES[order.statusBucket]"
              >
                {{ order.statusLabel || '—' }}
              </span>
            </td>
            <td class="hidden px-4 py-3 lg:table-cell text-n-slate-11">
              {{ order.paymentType || '—' }}
            </td>
            <td class="hidden px-4 py-3 sm:table-cell sm:px-6">
              <span
                class="inline-flex items-center gap-1.5 text-xs font-medium"
                :class="SOURCES[order.source].badge.split(' ')[1]"
              >
                <span
                  class="rounded-full size-2"
                  :class="SOURCES[order.source].dot"
                />
                {{ SOURCES[order.source].label }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Sayfalama: yalnızca normal listelemede gösterilir, arama sonuçlarında gösterilmez -->
    <footer
      v-if="!isSearchMode && !isLoading && pageOrders.length"
      class="flex items-center justify-between gap-3 px-4 py-3 border-t sm:px-6 border-n-weak flex-shrink-0"
    >
      <button
        type="button"
        class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg text-n-slate-11 outline outline-1 outline-n-weak hover:bg-n-alpha-1 disabled:opacity-40 disabled:cursor-not-allowed"
        :disabled="currentPage <= 1 || isLoadingMore"
        @click="goToPage(currentPage - 1)"
      >
        <span class="size-4 i-lucide-chevron-left" />
        {{ t('ORDERS.PAGINATION.PREV') }}
      </button>
      <span class="flex items-center gap-2 text-sm text-n-slate-11">
        <span
          v-if="isLoadingMore"
          class="size-3.5 border-2 rounded-full animate-spin border-n-slate-8 border-t-transparent"
        />
        {{ t('ORDERS.PAGINATION.PAGE', { page: currentPage }) }}
      </span>
      <button
        type="button"
        class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg text-n-slate-11 outline outline-1 outline-n-weak hover:bg-n-alpha-1 disabled:opacity-40 disabled:cursor-not-allowed"
        :disabled="!hasMore || isLoadingMore"
        @click="goToPage(currentPage + 1)"
      >
        {{ t('ORDERS.PAGINATION.NEXT') }}
        <span class="size-4 i-lucide-chevron-right" />
      </button>
    </footer>
  </div>
</template>
